import { queriesRequirements } from "@query";
import { WSQueryResult } from "@ws-react-query";
import {
  PayeeRequirement,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";

import { EligibilityRequirements } from "../steps/EligibilityRequirements/EligibilityRequirements";
import {
  OnboardingContext,
  OnboardingModuleEligibilityRequirements
} from "../types";
import { selectorIsAllRequirementsComplete } from "../../Requirements/selectors/selectorIsAllRequirementsComplete";
import { getIsGeneralRequirement } from "../../Requirements/utils";
import {
  buildOnboardingStep,
  UseCreateOnboardingStep,
  useOnboardingStepQueryConfig
} from "../components/OnboardingFlowUtils";
import { useSession } from "@hooks/useSession";
import { FlowStep } from "../../../shared/components/FlowSwitch/types";

const getStatus = (requirements: PayeeRequirement[]) => {
  if (selectorIsAllRequirementsComplete(requirements)) {
    return "Complete";
  }

  return "None";
};

export const getEligibilityRequirementsStepConfig = (
  _: OnboardingModuleEligibilityRequirements,
  __: OnboardingContext,
  requirements?: PayeeRequirement[]
): FlowStep | null => {
  const nonGeneralRequirements = requirements?.filter(
    r => !getIsGeneralRequirement(r)
  );

  // When data hasn't loaded yet (undefined), return a placeholder step
  // so loadStep can show a loading state instead of removing the step entirely.
  // Removing the step causes a redirect to the first onboarding step.
  if (requirements && !nonGeneralRequirements?.length) {
    return null;
  }

  const slug = "requirements";

  const status = nonGeneralRequirements
    ? getStatus(nonGeneralRequirements)
    : "None";

  return {
    title: "Additional client requirements",
    slug,
    component: EligibilityRequirements,

    status,
    result: {
      Complete: {
        title: "Your eligibility requirements are complete",
        description:
          "Your eligibility requirements are complete. Your eligibility requirements from your clients are complete. If additional requirements are needed in the future, these tasks will appear on your “Get started” homepage. Click continue for the next step."
      }
    }
  };
};

export const useEligibilityRequirementsStep: UseCreateOnboardingStep<
  OnboardingModuleEligibilityRequirements
> = modules => {
  const config = useOnboardingStepQueryConfig(
    modules,
    "eligibility_requirements"
  );
  const { accountId } = useSession();
  // TODO(v3-blocker): v1 filtered on `payerPayeeEngagementIds != null` (only
  // engagement-attached requirements) and sorted `updatedAt desc`; v3
  // `listPayeeRequirements` has neither an existence operator nor a sort
  // param, so both are dropped.
  const queryRequirements =
    queriesRequirements.listPayeeRequirements.useQueryAll(
      {
        filter: {
          payeeAccountId: { in: [accountId] },
          type: {
            notIn: [
              RequirementType.REGISTRATION,
              RequirementType.PAYOUT_METHOD,
              RequirementType.TAX_VERIFICATION
            ]
          }
        }
      },
      { ...config, enabled: config.enabled && !!accountId }
    );

  return (onboardingModule, contextValue) =>
    buildOnboardingStep({
      getConfig: getEligibilityRequirementsStepConfig,
      module: onboardingModule,
      context: contextValue,
      // the step config consumes the flattened collection, not the paged
      // envelope the query itself holds — the projection narrows the
      // query-result union, hence the cast
      queries: [
        {
          ...queryRequirements,
          data: queryRequirements.flatData
        } as unknown as WSQueryResult<PayeeRequirement[], unknown>
      ]
    });
};
